import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CATEGORIES, type DocCategory } from '@pti/domain';
import { api } from '../api';
import { useCan, useMe } from '../hooks';
import { fmtDate, fmtDateTime } from '../format';
import type { DocDetail, DocSummary, Person } from '../types';
import { ErrorNote, Icon, Kv, Loading, Modal, Pill, Section, Tabs, toast } from '../components/ui';
import { SignFlow } from '../components/SignFlow';

const STATUS: Record<DocSummary['status'], [string, string]> = {
  draft: ['Draft', 'muted'], requested: ['Upload requested', 'warn'], pending_verify: ['To verify', 'warn'], awaiting: ['Awaiting signature', 'warn'],
  completed: ['Signed', 'ok'], filed: ['Filed', 'ok'], void: ['Void', 'muted'],
};
const statusText = (d: DocSummary) => (d.status === 'awaiting' && d.nextSigner ? `Awaiting ${d.nextSigner.label.replace(' — acknowledge', '')}` : STATUS[d.status][0]);

export function DocTable({ docs, showPerson }: { docs: DocSummary[]; showPerson?: boolean }) {
  const nav = useNavigate();
  if (!docs.length) return <p className="empty">No documents.</p>;
  return (
    <div className="table-wrap">
      <table className="tbl click">
        <thead><tr><th>Document</th>{showPerson && <th>Person</th>}<th>Sent</th><th>Status</th></tr></thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} tabIndex={0} onClick={() => nav(`/documents/${d.id}`)} onKeyDown={(e) => e.key === 'Enter' && nav(`/documents/${d.id}`)}>
              <td><span className="doc-t"><Icon n="doc" s={16} /><span><strong>{d.title}</strong><span className="small dim block">{d.reference} · {d.categoryLabel}</span></span></span></td>
              {showPerson && <td>{d.personName}</td>}
              <td className="num small">{fmtDate(d.sentAt ?? d.createdAt)}</td>
              <td><Pill tone={STATUS[d.status][1]}>{statusText(d)}</Pill></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Documents() {
  const can = useCan();
  const [filter, setFilter] = useState<'open' | 'done' | 'all'>('open');
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const q = useQuery({ queryKey: ['documents'], queryFn: () => api.get<DocSummary[]>('/api/documents') });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const rows = q.data!.filter((d) => filter === 'all' || (filter === 'open' ? ['draft', 'awaiting', 'requested', 'pending_verify'].includes(d.status) : ['completed', 'filed'].includes(d.status)));
  const count = (s: DocSummary['status'][]) => q.data!.filter((d) => s.includes(d.status)).length;
  return (
    <div className="page">
      <div className="page-h">
        <div><p className="eyebrow">Across the lifecycle</p><h1>Documents & signing</h1><p className="lede">Contracts, variations, position descriptions, letters and certificates — signed electronically and filed against the person.</p></div>
        <div className="row gap wrap-row">
          {can('documents:bulk_import') && <button className="btn" onClick={() => setBulk(true)}>Bulk import</button>}
          {can('documents:write') && <button className="btn primary" onClick={() => setAdding(true)}>+ Add document</button>}
        </div>
      </div>
      <div className="tiles four">
        <div className="tile"><span className="tile-n tone-warn">{count(['awaiting'])}</span><span>Awaiting signature</span></div>
        <div className="tile"><span className="tile-n">{count(['draft'])}</span><span>Drafts</span></div>
        <div className="tile"><span className="tile-n tone-warn">{count(['pending_verify', 'requested'])}</span><span>To verify</span></div>
        <div className="tile"><span className="tile-n tone-ok">{count(['completed', 'filed'])}</span><span>Signed or filed</span></div>
      </div>
      <div className="chips">{(['open', 'done', 'all'] as const).map((k) => <button key={k} className={`chip${filter === k ? ' on' : ''}`} onClick={() => setFilter(k)}>{k === 'open' ? 'In progress' : k === 'done' ? 'Completed' : 'All'}</button>)}</div>
      <DocTable docs={rows} showPerson />
      {adding && <AddDocument onClose={() => setAdding(false)} />}
      {bulk && <BulkImport onClose={() => setBulk(false)} />}
    </div>
  );
}

function AddDocument({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const people = useQuery({ queryKey: ['people'], queryFn: () => api.get<Person[]>('/api/people') });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api.get<{ key: string; name: string; category: string; signers: string[]; body?: string }[]>('/api/templates') });
  const [mode, setMode] = useState<'template' | 'upload'>('template');
  const [personId, setPersonId] = useState('');
  const [tplKey, setTplKey] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState({ title: '', category: 'contract' as DocCategory, signers: ['person', 'director'] as string[], dueDate: '', send: true });
  const tpl = templates.data?.find((t) => t.key === tplKey);
  // Fields the person record cannot fill, which the sender types in.
  const extra = tpl?.body ? [...new Set([...tpl.body.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1]!))].filter((k) => !['person.givenName', 'person.fullName', 'position.title', 'engagement.type', 'engagement.fte', 'start.date', 'last.date'].includes(k)) : [];
  const m = useMutation({
    mutationFn: async () => {
      if (mode === 'template') return api.send<{ id: number; missing: string[] }>('POST', '/api/documents/from-template', { templateKey: tplKey, personId, fields, dueDate: meta.dueDate || null, send: meta.send });
      const fd = new FormData();
      fd.append('meta', JSON.stringify({ personId, category: meta.category, title: meta.title || file!.name.replace(/\.[^.]+$/, ''), signers: meta.signers, dueDate: meta.dueDate || null, send: meta.send }));
      fd.append('file', file!);
      return api.upload<{ id: number }>('/api/documents/upload', fd);
    },
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['documents'] }); toast(meta.send ? 'Sent for signature' : 'Saved'); onClose(); nav(`/documents/${r.id}`); },
  });
  const toggleSigner = (s: string) => setMeta({ ...meta, signers: meta.signers.includes(s) ? meta.signers.filter((x) => x !== s) : [...meta.signers, s] });
  return (
    <Modal title="Add a document" onClose={onClose} wide>
      <Tabs value={mode} onChange={setMode} tabs={[{ id: 'template', label: 'From a template' }, { id: 'upload', label: 'Upload a file' }]} />
      <div className="new-doc">
        <div className="stack">
          <label className="field" htmlFor="d-person"><span>Person</span>
            <select id="d-person" value={personId} onChange={(e) => setPersonId(e.target.value)}><option value="" disabled>Choose…</option>{people.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          </label>
          {mode === 'template' ? (
            <div className="tpl-list">
              {templates.data?.map((t) => <button key={t.key} className={`tpl${t.key === tplKey ? ' on' : ''}`} onClick={() => setTplKey(t.key)}><strong>{t.name}</strong><span className="small dim">Signs: {t.signers.map((s) => (s === 'person' ? 'the person' : s === 'rtom' ? 'RTO Manager' : 'Director')).join(' → ')}</span></button>)}
            </div>
          ) : (
            <label htmlFor="file-in" className={`drop${file ? ' has' : ''}`}>
              <Icon n="doc" s={28} />{file ? <strong>{file.name}</strong> : <><strong>Choose a PDF to upload</strong><span className="small dim">Documents that need signing must be PDF. Word files can be filed without signing.</span></>}
              <input id="file-in" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
          )}
        </div>
        <div className="stack">
          {mode === 'template' ? extra.map((k) => (
            <label key={k} className="field" htmlFor={`f-${k}`}><span>{k.replace(/[.]/g, ' ')}</span><input id={`f-${k}`} className="txt" value={fields[k] ?? ''} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} /></label>
          )) : (
            <>
              <label className="field" htmlFor="d-title"><span>Title</span><input id="d-title" className="txt" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} /></label>
              <label className="field" htmlFor="d-cat"><span>Type</span><select id="d-cat" value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value as DocCategory })}>{Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></label>
              <div className="field"><span>Who signs, in order</span><div className="chips">{[['person', 'The person'], ['director', 'Director'], ['rtom', 'RTO Manager']].map(([k, l]) => <button key={k} className={`chip${meta.signers.includes(k!) ? ' on' : ''}`} onClick={() => toggleSigner(k!)}>{meta.signers.includes(k!) ? `${meta.signers.indexOf(k!) + 1}. ` : ''}{l}</button>)}</div><span className="small dim">None selected = file without signing.</span></div>
            </>
          )}
          <label className="field" htmlFor="d-due"><span>Sign by</span><input id="d-due" type="date" className="txt" value={meta.dueDate} onChange={(e) => setMeta({ ...meta, dueDate: e.target.value })} /></label>
          <label className="check" htmlFor="d-send"><input id="d-send" type="checkbox" checked={meta.send} onChange={(e) => setMeta({ ...meta, send: e.target.checked })} /> Send now (otherwise save as a draft to review)</label>
          {m.error && <ErrorNote error={m.error} />}
          <div className="row gap"><button className="btn primary" disabled={!personId || (mode === 'template' ? !tplKey : !file) || m.isPending} onClick={() => m.mutate()}>{meta.send ? 'Send' : 'Save draft'}</button><button className="btn" onClick={onClose}>Cancel</button></div>
        </div>
      </div>
    </Modal>
  );
}

interface Guess { fileName: string; personId: string | null; confidence: 'high' | 'check' | 'none'; category: DocCategory; signedOn: string | null; dateIsYearOnly: boolean }

function BulkImport({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const people = useQuery({ queryKey: ['people'], queryFn: () => api.get<Person[]>('/api/people') });
  const [files, setFiles] = useState<File[]>([]);
  const [rows, setRows] = useState<Guess[]>([]);
  const [results, setResults] = useState<{ fileName: string; ok: boolean; reference?: string; error?: string }[] | null>(null);
  const [drag, setDrag] = useState(false);
  const add = async (list: File[]) => {
    const fresh = list.filter((f) => !files.some((x) => x.name === f.name));
    setFiles([...files, ...fresh]);
    const guesses = await api.send<Guess[]>('POST', '/api/documents/bulk-import/preview', { fileNames: fresh.map((f) => f.name) });
    setRows([...rows, ...guesses]);
  };
  const upd = (name: string, p: Partial<Guess>) => setRows(rows.map((r) => (r.fileName === name ? { ...r, ...p, confidence: p.personId ? 'high' : r.confidence, dateIsYearOnly: p.signedOn ? false : r.dateIsYearOnly } : r)));
  const ready = rows.filter((r) => r.personId && r.signedOn);
  const m = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('manifest', JSON.stringify(ready.map(({ fileName, personId, category, signedOn }) => ({ fileName, personId, category, signedOn }))));
      for (const r of ready) fd.append('files', files.find((f) => f.name === r.fileName)!);
      return api.upload<typeof results>('/api/documents/bulk-import', fd);
    },
    onSuccess: (r) => { setResults(r); void qc.invalidateQueries({ queryKey: ['documents'] }); },
  });
  if (results) return (
    <Modal title="Bulk import — results" onClose={onClose} wide>
      <ul className="checks">{results.map((r) => <li key={r.fileName} className={r.ok ? 'ok' : 'bad'}><Icon n={r.ok ? 'check' : 'x'} s={15} /><span><strong>{r.fileName}</strong> — {r.ok ? `filed as ${r.reference}` : r.error}</span></li>)}</ul>
      <button className="btn primary" onClick={onClose}>Done</button>
    </Modal>
  );
  return (
    <Modal title="Bulk import signed documents" onClose={onClose} wide>
      <p className="small">For contracts and letters already signed on paper. They are filed as <strong>signed (historical)</strong> against each person and appear in their library.</p>
      <label htmlFor="bulk-in" className={`drop slim${drag ? ' over' : ''}`} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); void add([...e.dataTransfer.files]); }}>
        <Icon n="doc" s={24} /><strong>Drop files here, or click to choose</strong>
        <span className="small dim">Name files <span className="ref">Surname_Firstname_Type_YYYY-MM-DD.pdf</span> and matching is automatic</span>
        <input id="bulk-in" type="file" multiple accept=".pdf,.doc,.docx,.jpg,.png" hidden onChange={(e) => void add([...(e.target.files ?? [])])} />
      </label>
      {rows.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="tbl bulk">
              <thead><tr><th>File</th><th>Person</th><th>Type</th><th>Signed on</th><th /></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.fileName}>
                  <td className="wrap"><span className="small mono-wrap">{r.fileName}</span></td>
                  <td>
                    <select aria-label="Person" value={r.personId ?? ''} onChange={(e) => upd(r.fileName, { personId: e.target.value })}><option value="">Choose…</option>{people.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
                    <span className="block">{r.confidence === 'high' ? <Pill tone="ok">Matched</Pill> : r.confidence === 'check' ? <Pill tone="warn">Check match</Pill> : <Pill tone="bad">No match</Pill>}</span>
                  </td>
                  <td><select aria-label="Type" value={r.category} onChange={(e) => upd(r.fileName, { category: e.target.value as DocCategory })}>{Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></td>
                  <td><input aria-label="Signed on" type="date" className="txt" value={r.signedOn ?? ''} onChange={(e) => upd(r.fileName, { signedOn: e.target.value })} />{r.dateIsYearOnly && <span className="small tone-warn block">Year only — confirm date</span>}</td>
                  <td><button className="icon-btn" aria-label="Remove" onClick={() => { setRows(rows.filter((x) => x.fileName !== r.fileName)); setFiles(files.filter((f) => f.name !== r.fileName)); }}><Icon n="x" s={15} /></button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          {m.error && <ErrorNote error={m.error} />}
          <div className="row between wrap-row">
            <span className="small">{ready.length} of {rows.length} ready{rows.length - ready.length ? ` · ${rows.length - ready.length} need a person or date` : ''}</span>
            <div className="row gap"><button className="btn primary" disabled={!ready.length || m.isPending} onClick={() => m.mutate()}>{m.isPending ? 'Importing…' : `Import ${ready.length}`}</button><button className="btn" onClick={onClose}>Cancel</button></div>
          </div>
        </>
      )}
    </Modal>
  );
}

export function DocumentPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const can = useCan();
  const me = useMe();
  const [signing, setSigning] = useState(false);
  const q = useQuery({ queryKey: ['document', id], queryFn: () => api.get<DocDetail>(`/api/documents/${id}`) });
  const act = (path: string, msg: string) => ({ mutationFn: () => api.send('POST', `/api/documents/${id}/${path}`), onSuccess: () => { void qc.invalidateQueries(); toast(msg); }, onError: (e: Error) => toast(e.message) });
  const send = useMutation(act('send', 'Sent for signature'));
  const verify = useMutation(act('verify', 'Verified'));
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const d = q.data!;
  const u = me.data!;
  const next = d.nextSigner;
  const myTurn = !!next && ((next.signerType === 'person' && next.personId === u.person?.id) || (next.signerType === 'director' && u.user.roles.includes('Director')) || (next.signerType === 'rtom' && u.user.roles.includes('RTOManager')));
  const open = () => api.openFile(`/api/documents/${id}/file`).catch((e: Error) => toast(e.message));
  return (
    <div className="page">
      <Link className="link back" to={can('documents:read') ? '/documents' : '/me/documents'}><Icon n="back" s={15} /> Documents</Link>
      <div className="page-h"><div><p className="eyebrow">{d.reference} · {d.categoryLabel}</p><h1>{d.title}</h1><p className="lede">{d.personName}</p></div>
        <Pill tone={STATUS[d.status][1]}>{statusText(d)}</Pill></div>
      <div className="grid-side wide">
        <Section title="Document">
          {signing ? (
            <SignFlow signerName={next?.signerType === 'person' ? d.personName : u.user.name} body={d.body} onOpenFile={open}
              onSubmit={async (s) => { await api.send('POST', `/api/documents/${id}/sign`, s); void qc.invalidateQueries(); }}
              onCancel={() => setSigning(false)} extraStep="You are signed in with your PTI Microsoft account, which confirms it is you." />
          ) : (
            <>
              {d.body ? <div className="paper"><div className="paper-h"><span className="mark sm" aria-hidden="true">P</span><span><strong>Perth Training Institute Pty Ltd</strong><span className="small dim block">RTO 52730 · 5/15 Kalinga Way, Landsdale WA 6065</span></span></div><div className="paper-body">{d.body.split('\n').filter(Boolean).map((t, i) => <p key={i}>{t}</p>)}</div></div>
                : <div className="paper-file"><Icon n="doc" s={36} /><strong>{d.hasFile ? 'PDF document' : 'No file'}</strong></div>}
              <div className="row gap wrap-row">
                {myTurn && d.status === 'awaiting' && <button className="btn primary" onClick={() => setSigning(true)}>Review and sign</button>}
                <button className="btn" onClick={open}>{d.status === 'completed' ? 'Open signed PDF' : 'Open PDF'}</button>
                {d.status === 'draft' && can('documents:write') && <button className="btn primary" disabled={send.isPending} onClick={() => send.mutate()}>Send for signature</button>}
                {d.status === 'pending_verify' && can('documents:verify') && <button className="btn primary" disabled={verify.isPending} onClick={() => verify.mutate()}>Verify</button>}
              </div>
            </>
          )}
        </Section>
        <div className="stack">
          <Section title="Signing order">
            {d.signers.length === 0 ? <p className="small dim">No signatures needed.</p> : (
              <ol className="signers">
                {d.signers.map((s) => {
                  const now = !s.signedAt && next?.label === s.label;
                  return <li key={s.seq} className={s.signedAt ? 'done' : now ? 'now' : ''}><span className="sg-dot">{s.signedAt ? <Icon n="check" s={11} /> : s.seq}</span><span><strong>{s.label}</strong><span className="small dim block">{s.signedAt ? `${fmtDateTime(s.signedAt)} · ${s.method}` : now ? 'Waiting' : 'Not yet'}</span></span></li>;
                })}
              </ol>
            )}
          </Section>
          <Section title="Details">
            <Kv items={[['Source', d.source.replace('_', ' ')], ['Created', fmtDateTime(d.createdAt)], ['Sign by', fmtDate(d.dueDate)], ['Retention', '7 years after engagement ends'], ...(d.verifiedBy ? [['Verified by', d.verifiedBy] as [string, string]] : [])]} />
            {d.sha256 && <p className="small dim mono-wrap">SHA-256 {d.sha256}</p>}
            {d.note && <p className="small">{d.note}</p>}
          </Section>
        </div>
      </div>
    </div>
  );
}
